// ============================================================================
// MetricsPanel — the computed KPI tiles from GET /api/clients/{id}/metrics.
// Optional-first / no-fake-zeros: each engine Result is rendered ONLY when
// ok===true (otherwise that tile is omitted, with a single honest note when
// nothing computes yet). Tiles animate their count-up via the design-system
// StatTile. BMI / WHR / WHtR carry a category Badge from their bands.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge, Card, CardHeader, EmptyState, StatTile } from "../../components";
import type { MetricsSummary } from "../../api";
import {
  bmiCategory,
  bmiTone,
  fmtNum,
  normalizeSex,
  resultValue,
  whrBand,
  whtrBand,
  type RiskTone,
} from "./lib";

// StatTile supports a subset of the chart accents (no amber edge).
type TileAccent = "coral" | "violet" | "ocean" | "lime";

interface MetricsPanelProps {
  metrics: MetricsSummary;
}

interface Tile {
  key: string;
  label: string;
  value: number;
  decimals: number;
  suffix?: string;
  accent: TileAccent;
  badge?: { label: string; tone: RiskTone };
}

export function MetricsPanel({ metrics }: MetricsPanelProps) {
  const { t } = useTranslation("clientDetail");
  const sex = normalizeSex(metrics.sex);

  const tiles: Tile[] = [];

  const bmi = resultValue(metrics.bmi);
  if (bmi != null) {
    const cat = bmiCategory(bmi);
    tiles.push({
      key: "bmi",
      label: t("metrics.bmi"),
      value: bmi,
      decimals: 1,
      accent: "coral",
      badge: { label: t(`bmiCat.${cat}`), tone: bmiTone(cat) },
    });
  }

  const bf = resultValue(metrics.body_fat);
  if (bf != null) {
    tiles.push({
      key: "body_fat",
      label: t("metrics.bodyFat"),
      value: bf,
      decimals: 1,
      suffix: "%",
      accent: "violet",
      badge:
        metrics.body_fat.note === "manual"
          ? { label: t("metrics.measured"), tone: "neutral" }
          : { label: t("metrics.estimated"), tone: "neutral" },
    });
  }

  const bmr = resultValue(metrics.bmr);
  if (bmr != null) {
    tiles.push({
      key: "bmr",
      label: t("metrics.bmr"),
      value: bmr,
      decimals: 0,
      suffix: " kcal",
      accent: "ocean",
    });
  }

  const tdee = resultValue(metrics.tdee);
  if (tdee != null) {
    tiles.push({
      key: "tdee",
      label: t("metrics.tdee"),
      value: tdee,
      decimals: 0,
      suffix: " kcal",
      accent: "lime",
    });
  }

  const lean = resultValue(metrics.lean_mass);
  if (lean != null) {
    tiles.push({
      key: "lean",
      label: t("metrics.leanMass"),
      value: lean,
      decimals: 1,
      suffix: " kg",
      accent: "lime",
    });
  }

  const fat = resultValue(metrics.fat_mass);
  if (fat != null) {
    tiles.push({
      key: "fat",
      label: t("metrics.fatMass"),
      value: fat,
      decimals: 1,
      suffix: " kg",
      accent: "coral",
    });
  }

  const whr = resultValue(metrics.whr);
  if (whr != null) {
    const band = whrBand(whr, sex);
    tiles.push({
      key: "whr",
      label: t("metrics.whr"),
      value: whr,
      decimals: 2,
      accent: "violet",
      badge: { label: t(`risk.${band.key}`), tone: band.tone },
    });
  }

  const whtr = resultValue(metrics.whtr);
  if (whtr != null) {
    const band = whtrBand(whtr);
    tiles.push({
      key: "whtr",
      label: t("metrics.whtr"),
      value: whtr,
      decimals: 2,
      accent: "ocean",
      badge: { label: t(`risk.${band.key}`), tone: band.tone },
    });
  }

  return (
    <Card>
      <CardHeader title={t("metrics.title")} subtitle={t("metrics.subtitle")} />
      {tiles.length === 0 ? (
        <EmptyState
          title={t("metrics.emptyTitle")}
          description={t("metrics.emptyBlurb")}
          className="!py-12"
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {tiles.map((tile) => (
            <div key={tile.key} className="relative">
              <StatTile
                label={tile.label}
                value={tile.value}
                decimals={tile.decimals}
                suffix={tile.suffix}
                accent={tile.accent}
              />
              {tile.badge && (
                <span className="mt-2 flex">
                  <Badge tone={tile.badge.tone}>{tile.badge.label}</Badge>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Compact inline metric chip — used in summaries (e.g. ideal-weight context). */
export function MetricChip({
  label,
  value,
  decimals = 1,
  suffix,
}: {
  label: string;
  value: number;
  decimals?: number;
  suffix?: string;
}) {
  return (
    <div className="rounded-md bg-surface-sunken px-3 py-2">
      <p className="text-[0.65rem] font-bold uppercase tracking-kicker text-body-soft">
        {label}
      </p>
      <p className="nums font-display text-lg font-extrabold tracking-tightest text-body">
        {fmtNum(value, decimals)}
        {suffix && <span className="ml-0.5 text-sm font-bold text-body-soft">{suffix}</span>}
      </p>
    </div>
  );
}
